/**
 * Neue Version anbieten (Mockup docs/mockups/app-aktualisieren.html, Auswahl 1D).
 *
 * Das iPhone startet eine installierte App beim Zurückkehren nicht neu — geprüft wurde bisher nur beim Start,
 * neuer Code kam erst nach dem Schließen der App. Jetzt prüft die App auch beim Zurückkehren und alle 30 Minuten.
 * Was sie mit einer gefundenen Version tut, entscheidet `entscheide`:
 *  - still neu laden, wenn niemand hinsieht (App im Hintergrund) oder sie gerade erst gestartet bzw. zurückgekehrt
 *    ist — und nichts offen oder eingetippt ist;
 *  - sonst die Karte „Neue Version bereit“ zeigen. Neu geladen wird erst auf Knopfdruck, „Später“ blendet sie bis
 *    zum nächsten Zurückkehren aus (dann greift wieder der stille Weg).
 */

/** So lange nach Start oder Rückkehr gilt ein Neuladen als Teil des Öffnens, nicht als Störung */
export const FRISCH_MS = 10_000;
/** Regelmäßige Prüfung, solange die App offen ist */
export const PRUEF_TAKT_MS = 30 * 60_000;
/** Beim Hin- und Herwechseln höchstens so oft prüfen */
export const PRUEF_MINDESTABSTAND_MS = 60_000;

export type Entscheidung = 'still' | 'karte';

export const entscheide = (z: { versteckt: boolean; msSeitFrisch: number; beschaeftigt: boolean }): Entscheidung =>
  !z.beschaeftigt && (z.versteckt || z.msSeitFrisch < FRISCH_MS) ? 'still' : 'karte';

/**
 * Felder, in die seit dem Laden getippt wurde. Solange eines davon noch auf der Seite steht und etwas enthält, könnte
 * die Eingabe ungespeichert sein — dann nie still neu laden. Ein abgeschicktes Formular (Anmeldung, gespeichertes
 * Fenster) verschwindet aus der Seite und zählt danach nicht mehr.
 */
const getippt = new Set<HTMLInputElement | HTMLTextAreaElement | HTMLElement>();
export const merkeEingaben = () => {
  document.addEventListener('input', (e) => { if (e.target instanceof HTMLElement) getippt.add(e.target); }, { capture: true });
};
const hatOffeneEingabe = (): boolean => {
  for (const el of [...getippt]) {
    if (!el.isConnected) { getippt.delete(el); continue; }
    const wert = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement ? el.value : el.textContent ?? '';
    if (wert.trim() !== '') return true;
  }
  return false;
};

/** Ein Fenster ist offen, ein Feld hat den Fokus oder ein Feld enthält noch Getipptes */
export const istBeschaeftigt = (doc: Document = document): boolean => {
  if (doc.querySelector('[aria-modal="true"], [data-fenster]')) return true;
  const fokus = doc.activeElement as HTMLElement | null;
  if (fokus && (fokus.matches('input, textarea, select') || fokus.isContentEditable === true)) return true;
  return hatOffeneEingabe();
};

// ---- Zustand der Karte (eine Quelle für die Komponente) ----

export interface KartenZustand { sichtbar: boolean; punkte: string[] }
let zustand: KartenZustand = { sichtbar: false, punkte: [] };
const hoerer = new Set<() => void>();
let ausfuehren: (() => void) | null = null;

export const kartenZustand = () => zustand;
export const abonniere = (f: () => void) => { hoerer.add(f); return () => { hoerer.delete(f); }; };
const setze = (neu: KartenZustand) => { zustand = neu; hoerer.forEach(f => f()); };

/** Karte zeigen; `aktualisieren` lädt die neue Version */
export const zeigeKarte = (punkte: string[], aktualisieren: () => void) => {
  ausfuehren = aktualisieren;
  setze({ sichtbar: true, punkte });
};
export const blendeAus = () => setze({ ...zustand, sichtbar: false });
export const aktualisiereJetzt = () => { setze({ ...zustand, sichtbar: false }); (ausfuehren ?? (() => window.location.reload()))(); };

/** Nur für Tests */
export const _zuruecksetzen = () => { getippt.clear(); ausfuehren = null; setze({ sichtbar: false, punkte: [] }); };
